/**
 * LoginPage - 登录/注册页面
 * 新版布局：左侧表单 + 右侧大图（渐变融合）
 * 支持后台可配置：Logo、软件名称、版本、背景图（5张随机）、右上角/右下角文字
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, AlertCircle, Loader2, Ticket, Eye, EyeOff, Sparkles, HardDrive, Zap, Wifi, X, Folder, Phone, ShieldCheck, KeyRound, Sun, Moon } from 'lucide-react';
import localCacheService from '../services/localCacheService';
import api from '../api';
import * as C from '../constants';
import { getLogos, readBrandingCache, writeBrandingCache } from '../utils/branding';
import { useTheme } from '../hooks/useTheme';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [theme, toggleTheme] = useTheme();

  const [isLogin, setIsLogin] = useState(true); // true=登录, false=注册
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(''); // 🆕 邀请码
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 🆕 密码显示/隐藏状态
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // 🆕 欢迎弹窗状态
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeUsername, setWelcomeUsername] = useState('');
  
  // 🆕 本地缓存提醒弹窗状态
  const [showCachePrompt, setShowCachePrompt] = useState(false);
  const [cachePromptLoading, setCachePromptLoading] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);

  // 📱 短信验证相关状态
  const [smsEnabled, setSmsEnabled] = useState(false); // 后台是否配置了短信服务
  const [showSmsModal, setShowSmsModal] = useState(false); // 注册时的短信验证弹窗
  const [smsPhone, setSmsPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsError, setSmsError] = useState('');
  const [smsVerifying, setSmsVerifying] = useState(false);

  // 📱 找回密码相关状态
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1:输入手机号→发送验证码  2:输入验证码  3:设置新密码  4:成功
  const [resetPhone, setResetPhone] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPwd, setResetNewPwd] = useState('');
  const [resetConfirmPwd, setResetConfirmPwd] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetCountdown, setResetCountdown] = useState(0);
  const [showResetPwd, setShowResetPwd] = useState(false);
  
  // 🎨 品牌配置 —— 从 localStorage 缓存初始化，避免 logo 闪烁
  const [branding, setBranding] = useState(() => {
    const defaults = {
      logos: [],
      logo: '',
      appName: 'Cineart Canvas',
      version: 'Studio 1.0',
      topRightText: '',
      bottomRightText: '',
      backgroundImages: [],
      cornerLogo: '',
      cornerLogoColor: '#ffffff',
      cornerLogoOpacity: 0.3
    };
    const cached = readBrandingCache();
    return cached ? { ...defaults, ...cached } : defaults;
  });
  
  // 🎨 Logo 列表 —— 最多 3 个并排。老配置只有单个 logo 字符串，getLogos 统一兼容
  const brandingLogos = useMemo(() => getLogos(branding), [branding]);

  // 登录背景图：仍保持“随机单图”策略，但增加失败兜底，避免卡在加载态。
  const bgCandidates = useMemo(
    () => (branding.backgroundImages || []).filter(Boolean),
    [branding.backgroundImages]
  );
  const [bgCandidateOrder, setBgCandidateOrder] = useState([]);
  const [bgCandidateIndex, setBgCandidateIndex] = useState(0);
  const [bgAllFailed, setBgAllFailed] = useState(false);

  // 🆕 背景图加载状态（渐进式加载效果）
  const [bgLoaded, setBgLoaded] = useState(false);

  const selectedBgImage = bgCandidateOrder[bgCandidateIndex] || '';

  const switchToNextBgCandidate = useCallback(() => {
    setBgLoaded(false);
    setBgCandidateIndex(prev => {
      if (prev + 1 < bgCandidateOrder.length) {
        return prev + 1;
      }
      setBgAllFailed(true);
      return prev;
    });
  }, [bgCandidateOrder.length]);

  useEffect(() => {
    if (bgCandidates.length === 0) {
      setBgCandidateOrder([]);
      setBgCandidateIndex(0);
      setBgAllFailed(true);
      setBgLoaded(true);
      return;
    }

    // 随机起点 + 环形顺序：先随机单图，失败时按顺序尝试其余候选。
    const start = Math.floor(Math.random() * bgCandidates.length);
    const ordered = [
      ...bgCandidates.slice(start),
      ...bgCandidates.slice(0, start)
    ];

    setBgCandidateOrder(ordered);
    setBgCandidateIndex(0);
    setBgAllFailed(false);
    setBgLoaded(false);
  }, [bgCandidates]);

  // 网络卡住但不触发 onError 时，超时后尝试下一张，避免一直停在 shimmer。
  useEffect(() => {
    if (!selectedBgImage || bgLoaded || bgAllFailed) return;
    const timer = setTimeout(() => {
      switchToNextBgCandidate();
    }, 10000);
    return () => clearTimeout(timer);
  }, [selectedBgImage, bgLoaded, bgAllFailed, switchToNextBgCandidate]);
  
  // 加载品牌配置并缓存
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await api.get('/api/settings/login-branding');
        if (data) {
          setBranding(prev => ({ ...prev, ...data }));
          writeBrandingCache(data);
        }
      } catch (err) {
        console.warn('加载登录品牌配置失败:', err);
      }
    };
    loadBranding();
  }, []);

  // 📱 检查短信服务是否已配置
  useEffect(() => {
    const checkSms = async () => {
      try {
        const res = await fetch(`${C.API_BASE_URL}/api/auth/sms-enabled`);
        const data = await res.json();
        setSmsEnabled(!!data.smsEnabled);
      } catch { /* ignore */ }
    };
    checkSms();
  }, []);

  // 📱 短信发送倒计时（注册场景）
  useEffect(() => {
    if (smsCountdown <= 0) return;
    const timer = setTimeout(() => setSmsCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [smsCountdown]);

  // 📱 短信发送倒计时（找回密码场景）
  useEffect(() => {
    if (resetCountdown <= 0) return;
    const timer = setTimeout(() => setResetCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resetCountdown]);

  // 📱 发送短信验证码（注册场景）
  const handleSmsSend = useCallback(async () => {
    if (smsSending || smsCountdown > 0) return;
    if (!smsPhone || !/^1[3-9]\d{9}$/.test(smsPhone)) {
      setSmsError('请输入正确的手机号');
      return;
    }
    setSmsSending(true);
    setSmsError('');
    try {
      const res = await fetch(`${C.API_BASE_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: smsPhone, scene: 'register' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '发送失败');
      setSmsCountdown(180);
    } catch (err) {
      setSmsError(err.message);
    } finally {
      setSmsSending(false);
    }
  }, [smsPhone, smsSending, smsCountdown]);

  // 📱 注册时提交短信验证 → 完成注册
  const handleSmsRegister = useCallback(async () => {
    if (!smsPhone || !/^1[3-9]\d{9}$/.test(smsPhone)) {
      setSmsError('请输入正确的手机号');
      return;
    }
    if (!smsCode || smsCode.length < 4) {
      setSmsError('请输入验证码');
      return;
    }
    setSmsVerifying(true);
    setSmsError('');
    try {
      await register(username.trim(), password, inviteCode.trim(), smsPhone, smsCode);
      setShowSmsModal(false);
      setWelcomeUsername(username.trim());
      setShowWelcome(true);
    } catch (err) {
      setSmsError(err.message || '注册失败');
    } finally {
      setSmsVerifying(false);
    }
  }, [smsPhone, smsCode, username, password, inviteCode, register]);

  // 📱 找回密码 — 发送验证码
  const handleResetSendCode = useCallback(async () => {
    if (resetLoading || resetCountdown > 0) return;
    if (!username.trim()) {
      setResetError('请先在登录页填写用户名');
      return;
    }
    if (!resetPhone || !/^1[3-9]\d{9}$/.test(resetPhone)) {
      setResetError('请输入正确的手机号');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      const res = await fetch(`${C.API_BASE_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: resetPhone, scene: 'reset', username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '发送失败');
      setResetCountdown(180);
      setResetStep(2);
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  }, [username, resetPhone, resetLoading, resetCountdown]);

  // 📱 找回密码 — 重置密码
  const handleResetPassword = useCallback(async () => {
    if (!resetCode || resetCode.length < 4) {
      setResetError('请输入验证码');
      return;
    }
    if (!resetNewPwd || resetNewPwd.length < 6) {
      setResetError('新密码长度至少 6 位');
      return;
    }
    if (resetNewPwd !== resetConfirmPwd) {
      setResetError('两次密码输入不一致');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      const res = await fetch(`${C.API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), phone: resetPhone, smsCode: resetCode, newPassword: resetNewPwd }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '重置失败');
      setResetStep(4); // 成功
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  }, [username, resetPhone, resetCode, resetNewPwd, resetConfirmPwd]);

  // 📱 关闭找回密码弹窗
  const closeResetModal = useCallback(() => {
    setShowResetModal(false);
    setResetStep(1);
    setResetPhone('');
    setResetCode('');
    setResetNewPwd('');
    setResetConfirmPwd('');
    setResetError('');
    setResetCountdown(0);
    setShowResetPwd(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // 基础校验
    if (!username.trim() || !password) {
      setError('请填写用户名和密码');
      return;
    }
    
    if (!isLogin && password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    
    // 📱 注册时若开启短信验证，先做完整预校验再弹出短信验证弹窗
    if (!isLogin && smsEnabled) {
      // 前端基础校验
      if (password.length < 6) {
        setError('密码长度至少6位');
        return;
      }
      if (!inviteCode.trim()) {
        setError('请填写邀请码');
        return;
      }
      // 后端验证邀请码是否有效
      try {
        const res = await fetch(`${C.API_BASE_URL}/api/auth/validate-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviteCode: inviteCode.trim() })
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || '邀请码无效');
          return;
        }
      } catch {
        setError('校验邀请码失败，请检查网络');
        return;
      }
      setSmsPhone('');
      setSmsCode('');
      setSmsError('');
      setShowSmsModal(true);
      return;
    }
    
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(username.trim(), password);
        // 🎬 检查是否有待处理的项目组邀请
        const pendingInvite = localStorage.getItem('pendingCrewInvite');
        let targetPath = '/';
        if (pendingInvite) {
          localStorage.removeItem('pendingCrewInvite');
          targetPath = `/join/${pendingInvite}`;
        }
        
        // 🆕 检查是否已配置本地缓存，并验证配置有效性
        if (localCacheService.isFileSystemAccessSupported()) {
          const validation = await localCacheService.validateCacheConfig();

          if (!validation.valid) {
            if (validation.needReauth) {
              // 权限过期，不阻拦登录，进入后由横幅引导一键恢复
              console.log('ℹ️ 本地缓存权限过期，进入后可一键恢复');
            } else {
              if (validation.needReselect) {
                await localCacheService.clearCacheConfig();
                console.log('⚠️ 缓存文件夹已删除，已清除配置');
              }
              setPendingNavigate(targetPath);
              setShowCachePrompt(true);
              return;
            }
          }
        }

        navigate(targetPath);
      } else {
        await register(username.trim(), password, inviteCode.trim());
        setWelcomeUsername(username.trim());
        setShowWelcome(true);
      }
    } catch (err) {
      setError(err.message || (isLogin ? '登录失败' : '注册失败'));
    } finally {
      setLoading(false);
    }
  };
  
  // 🆕 关闭欢迎弹窗并跳转
  const handleWelcomeClose = async () => {
    setShowWelcome(false);
    const pendingInvite = localStorage.getItem('pendingCrewInvite');
    let targetPath = '/';
    if (pendingInvite) {
      localStorage.removeItem('pendingCrewInvite');
      targetPath = `/join/${pendingInvite}`;
    }
    
    if (localCacheService.isFileSystemAccessSupported()) {
      const validation = await localCacheService.validateCacheConfig();
      if (!validation.valid) {
        if (validation.needReauth) {
          // 权限过期，不阻拦，进入后由横幅引导一键恢复
          navigate(targetPath);
        } else {
          if (validation.needReselect) {
            await localCacheService.clearCacheConfig();
          }
          setPendingNavigate(targetPath);
          setShowCachePrompt(true);
        }
      } else {
        navigate(targetPath);
      }
    } else {
      navigate(targetPath);
    }
  };
  
  // 🆕 启用本地缓存
  const handleEnableCache = async () => {
    setCachePromptLoading(true);
    const result = await localCacheService.requestCacheFolder();
    setCachePromptLoading(false);
    
    if (result.success || result.error === '已取消选择') {
      setShowCachePrompt(false);
      navigate(pendingNavigate || '/');
    } else {
      alert(result.error);
      setShowCachePrompt(false);
      navigate(pendingNavigate || '/');
    }
  };
  
  // 🆕 稍后配置缓存
  const handleSkipCache = () => {
    setShowCachePrompt(false);
    navigate(pendingNavigate || '/');
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setConfirmPassword('');
    setInviteCode('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setSmsPhone('');
    setSmsCode('');
    setSmsError('');
  };


  /**
   * 渲染主界面 
   * 结构调整：
   * 1. 外层容器 (cardContainer) 设定为 21:9 电影画幅，直角
   * 2. 底层 (bgLayer) 全屏显示背景图
   * 3. 顶层 (contentLayer) 覆盖左侧渐变遮罩 + 内容
   */
  return (
    <div style={{
      ...styles.container,
      // 🎨 浅色模式：外框改浅色底（品牌 hero 卡片仍保持深色，是品牌照片上的压暗层）
      ...(theme === 'light' ? {
        background: `
          radial-gradient(circle at 10% 90%, rgba(203, 213, 225, 0.5) 0%, transparent 40%),
          radial-gradient(circle at 90% 10%, rgba(203, 213, 225, 0.35) 0%, transparent 40%),
          #f5f5f4
        `,
      } : {}),
    }}>
      {/* 🎨 左下角深浅色切换按钮（与编辑器左下角同款圆形） */}
      <button
        onClick={toggleTheme}
        title={theme === 'light' ? '浅色模式（点击切换深色）' : '深色模式（点击切换浅色）'}
        style={{
          position: 'absolute', bottom: 20, left: 20, zIndex: 100,
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-panel)', color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-hover)'; e.currentTarget.style.borderColor = 'var(--text-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
      >
        {theme === 'light' ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
      </button>
      {/* 21:9 电影级画幅容器 */}
      <div style={styles.cardContainer}>
        
        {/* 底层：背景图 (由管理员上传) */}
        <div style={styles.bgLayer}>
          {selectedBgImage && !bgAllFailed ? (
            <>
              {/* 背景图容器 - 带加载动画 */}
              <img 
                src={selectedBgImage} 
                alt="Background" 
                style={{
                  ...styles.bgImage,
                  filter: bgLoaded ? 'none' : 'blur(20px) brightness(0.8)',
                  transform: bgLoaded ? 'scale(1)' : 'scale(1.1)',
                  opacity: bgLoaded ? 1 : 0.6,
                  transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onLoad={() => setBgLoaded(true)}
                onError={() => switchToNextBgCandidate()}
              />
              {/* 🆕 加载中闪光效果 */}
              {!bgLoaded && (
                <div style={styles.shimmerOverlay}>
                  <div style={styles.shimmerBar} />
                </div>
              )}
            </>
          ) : (
            <div style={styles.placeholderBg}>
              <div style={styles.bgDecorCircle} />
            </div>
          )}
          {/* 全局叠加一个微暗的遮罩，确保文字可读性，但主要靠左侧渐变 */}
          <div style={styles.globalOverlay} />
        </div>

        {/* 顶层：左侧渐变面板 + 内容 */}
        <div style={styles.overlayPanel}>
          {/* 渐变背景：左侧深色 -> 右侧透明 */}
          <div style={styles.gradientBackground} />

          {/* 内容区域：限制在左侧 1/3 左右 */}
          <div style={styles.contentArea}>

            {/* Logo 和标题 */}
            <div style={styles.headerSection}>
              {/* Logo 容器始终占位，避免布局跳动；多个 Logo 从左到右并排，间距 2px */}
              <div style={styles.logoContainer}>
                {brandingLogos.map((url, index) => (
                  <img
                    key={`${url}-${index}`}
                    src={url}
                    alt="Logo"
                    style={styles.customLogo}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
              <div style={styles.titleGroup}>
                <h1 style={styles.appName}>{branding.appName}</h1>
                {branding.version && (
                  <p style={styles.version}>{branding.version}</p>
                )}
              </div>
            </div>

            {/* 登录表单 */}
            <form onSubmit={handleSubmit} style={styles.form}>
              {/* 用户名 */}
              <div style={styles.inputGroup}>
                <User size={18} style={styles.inputIcon} />
                <input
                  type="text"
                  placeholder="用户名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  style={styles.input}
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
              
              {/* 密码 */}
              <div style={styles.inputGroup}>
                <Lock size={18} style={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{...styles.input, paddingRight: '44px'}}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeBtn}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {/* 确认密码（仅注册） */}
              {!isLogin && (
                <div style={styles.inputGroup}>
                  <Lock size={18} style={styles.inputIcon} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="确认密码"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{...styles.input, paddingRight: '44px'}}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={styles.eyeBtn}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              )}
              
              {/* 邀请码（仅注册） */}
              {!isLogin && (
                <div style={styles.inputGroup}>
                  <Ticket size={18} style={styles.inputIcon} />
                  <input
                    type="text"
                    placeholder="邀请码（必须使用邀请注册）"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    style={{...styles.input, letterSpacing: '1px', fontFamily: 'Monaco, monospace'}}
                    disabled={loading}
                    maxLength={8}
                  />
                </div>
              )}
              
              {/* 错误提示 — 始终占位避免跳动 */}
              <div style={{ minHeight: 20, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                {error && (
                  <>
                    <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
                    <span style={{ color: '#ef4444' }}>{error}</span>
                  </>
                )}
              </div>
              
              {/* 按钮区域 */}
              <div style={styles.actionRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" onClick={switchMode} style={styles.textLinkBtn} disabled={loading}>
                     {isLogin ? '注册账号' : '已有账号？登录'}
                  </button>
                  {isLogin && smsEnabled && error && (
                    <>
                      <span style={{ color: '#333', fontSize: 12 }}>|</span>
                      <button
                        type="button"
                        onClick={() => { setShowResetModal(true); setResetStep(1); setResetError(''); setResetPhone(''); }}
                        style={{ ...styles.textLinkBtn, color: '#60a5fa' }}
                        disabled={loading}
                      >
                        找回密码
                      </button>
                    </>
                  )}
                </div>

                <button type="submit" style={styles.submitBtn} disabled={loading}>
                  {loading ? <Loader2 size={18} className="spin" /> : (isLogin ? '登录' : '创建')}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* 🆕 右角标区域 (SVG 或图片) */}
        {branding.cornerLogo && (
          <div style={styles.cornerLogoSection}>
            <img 
              src={branding.cornerLogo} 
              alt=""
              onError={(e) => { e.target.style.display = 'none'; }}
              style={{
                ...styles.cornerLogoImg,
                opacity: branding.cornerLogoOpacity || 0.3,
                filter: 
                  branding.cornerLogoColor === '#ffffff' ? 'brightness(0) invert(1)' 
                  : branding.cornerLogoColor === '#9ca3af' ? 'brightness(0) invert(0.6)'
                  : branding.cornerLogoColor === '#000000' ? 'brightness(0) invert(0)'
                  : branding.cornerLogoColor === '#ef4444' ? 'brightness(0) saturate(100%) invert(38%) sepia(94%) saturate(3498%) hue-rotate(345deg) brightness(96%) contrast(92%)'
                  : branding.cornerLogoColor === '#fbbf24' ? 'brightness(0) saturate(100%) invert(79%) sepia(61%) saturate(555%) hue-rotate(359deg) brightness(102%) contrast(97%)'
                  : branding.cornerLogoColor === '#3b82f6' ? 'brightness(0) saturate(100%) invert(46%) sepia(98%) saturate(1677%) hue-rotate(207deg) brightness(100%) contrast(93%)'
                  : branding.cornerLogoColor === '#10b981' ? 'brightness(0) saturate(100%) invert(66%) sepia(38%) saturate(3260%) hue-rotate(118deg) brightness(91%) contrast(86%)'
                  : 'brightness(0) invert(1)'
              }}
            />
          </div>
        )}

        {/* 相关机构或组织名 (可配置) */}
        {branding.organizations && branding.organizations.length > 0 && (
          <div style={styles.organizationsSection}>
            {branding.organizations.map((org, index) => (
              <div key={index} style={styles.organizationItem}>
                {org.logo && (
                  <img 
                    src={org.logo} 
                    alt=""
                    onError={(e) => { e.target.style.display = 'none'; }}
                    style={styles.organizationLogo}
                  />
                )}
                {org.name && (
                  <span style={styles.organizationName}>{org.name}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 右上角 */}
         <div style={styles.cornerTextTopRight}>
          {branding.topRightText || 'Cineart Canvas Studio'}
         </div>

      </div>
      
      {/* 🆕 欢迎弹窗 */}
      {showWelcome && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalIcon}>
              <Sparkles size={48} color="#fbbf24" />
            </div>
            <h2 style={styles.modalTitle}>欢迎来到 {branding.appName}</h2>
            <p style={styles.modalSubtitle}>创意图板已就绪</p>
            <p style={styles.modalText}>
              Hi，<strong>{welcomeUsername}</strong>！<br />
              开始创作属于你的视觉故事吧！
            </p>
            <button onClick={handleWelcomeClose} style={{...styles.modalBtn, background: '#3b82f6'}}>
              开始探索
            </button>
          </div>
        </div>
      )}
      
      {/* 🆕 本地缓存提醒弹窗 */}
      {showCachePrompt && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <button onClick={handleSkipCache} style={styles.closeBtn}>
              <X size={18} />
            </button>
            <div style={styles.modalIcon}>
              <HardDrive size={48} color="#60a5fa" />
            </div>
            <h2 style={styles.modalTitle}>请配置本地缓存</h2>
            <div style={styles.featureList}>
              <div style={styles.featureItem}><Folder size={16} /><span>素材直连查找</span></div>
              <div style={styles.featureItem}><Zap size={16} /><span>加载提速 10 倍</span></div>
              <div style={styles.featureItem}><Wifi size={16} /><span>支持离线浏览</span></div>
            </div>
            <div style={styles.buttonGroup}>
              <button onClick={handleEnableCache} style={styles.primaryBtn} disabled={cachePromptLoading}>
                {cachePromptLoading ? '请稍候...' : '选择本地文件夹'}
              </button>
              <button onClick={handleSkipCache} style={styles.secondaryBtn}>
                稍后再说
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📱 注册短信验证弹窗 */}
      {showSmsModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <button onClick={() => setShowSmsModal(false)} style={styles.closeBtn}>
              <X size={18} />
            </button>
            <div style={styles.modalIcon}>
              <ShieldCheck size={48} color="#60a5fa" />
            </div>
            <h2 style={styles.modalTitle}>手机验证</h2>
            <p style={styles.modalSubtitle}>绑定手机号以完成注册</p>

            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {/* 用户名（只读） */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <User size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                <input
                  type="text"
                  value={username}
                  readOnly
                  style={{ ...smsInputStyle, paddingLeft: 36, flex: 1, background: 'var(--bg-hover)', color: 'var(--text-secondary)', cursor: 'default' }}
                />
              </div>
              {/* 手机号输入 */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Phone size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                <input
                  type="tel"
                  placeholder="手机号"
                  value={smsPhone}
                  onChange={e => setSmsPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  maxLength={11}
                  style={{ ...smsInputStyle, paddingLeft: 36, flex: 1 }}
                  disabled={smsVerifying}
                />
                <button
                  onClick={handleSmsSend}
                  disabled={smsSending || smsCountdown > 0 || smsVerifying}
                  style={{
                    marginLeft: 8, padding: '8px 14px', borderRadius: 6, border: 'none',
                    background: smsCountdown > 0 ? 'var(--bg-hover)' : 'rgba(59,130,246,0.2)',
                    color: smsCountdown > 0 ? 'var(--text-muted)' : '#60a5fa',
                    fontSize: 12, cursor: smsCountdown > 0 ? 'default' : 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 500,
                  }}
                >
                  {smsSending ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s 后重发` : '发送验证码'}
                </button>
              </div>

              {/* 验证码输入 */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <KeyRound size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                <input
                  type="text"
                  placeholder="短信验证码"
                  value={smsCode}
                  onChange={e => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  style={{ ...smsInputStyle, paddingLeft: 36, letterSpacing: 1, fontFamily: 'Monaco, monospace' }}
                  disabled={smsVerifying}
                />
              </div>

              {/* 错误提示 */}
              {smsError && (
                <div style={{ fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertCircle size={12} />
                  <span>{smsError}</span>
                </div>
              )}
            </div>

            <button
              onClick={handleSmsRegister}
              disabled={smsVerifying}
              style={{
                width: '100%', padding: 12, background: smsVerifying ? '#1e3a5f' : '#2563eb',
                color: '#fff', border: 'none', borderRadius: 8, cursor: smsVerifying ? 'wait' : 'pointer',
                fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              {smsVerifying ? <><Loader2 size={16} className="spin" /> 验证中...</> : '验证并注册'}
            </button>
          </div>
        </div>
      )}

      {/* 📱 找回密码弹窗 */}
      {showResetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <button onClick={closeResetModal} style={styles.closeBtn}>
              <X size={18} />
            </button>
            <div style={styles.modalIcon}>
              <KeyRound size={48} color={resetStep === 4 ? '#10b981' : '#f59e0b'} />
            </div>

            {/* Step 1: 输入手机号 */}
            {resetStep === 1 && (
              <>
                <h2 style={styles.modalTitle}>找回密码</h2>
                <p style={styles.modalSubtitle}>验证 <span style={{ color: '#f59e0b', fontWeight: 600 }}>{username || '—'}</span> 的手机号</p>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                  {/* 用户名（只读） */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <User size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                    <input
                      type="text"
                      value={username}
                      readOnly
                      style={{ ...smsInputStyle, paddingLeft: 36, width: '100%', background: 'var(--bg-hover)', color: 'var(--text-secondary)', cursor: 'default' }}
                    />
                  </div>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Phone size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                    <input
                      type="tel"
                      placeholder="手机号"
                      value={resetPhone}
                      onChange={e => setResetPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      maxLength={11}
                      style={{ ...smsInputStyle, paddingLeft: 36, width: '100%' }}
                      disabled={resetLoading}
                      onKeyDown={e => e.key === 'Enter' && handleResetSendCode()}
                    />
                  </div>
                  {resetError && (
                    <div style={{ fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <AlertCircle size={12} /><span>{resetError}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleResetSendCode}
                  disabled={resetLoading}
                  style={{
                    width: '100%', padding: 12, background: resetLoading ? '#4a3800' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', border: 'none', borderRadius: 8, cursor: resetLoading ? 'wait' : 'pointer',
                    fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {resetLoading ? <><Loader2 size={16} className="spin" /> 发送中...</> : '发送验证码'}
                </button>
              </>
            )}

            {/* Step 2: 输入验证码 */}
            {resetStep === 2 && (
              <>
                <h2 style={styles.modalTitle}>输入验证码</h2>
                <p style={styles.modalSubtitle}>已发送至 {resetPhone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}</p>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <KeyRound size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                    <input
                      type="text"
                      placeholder="短信验证码"
                      value={resetCode}
                      onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      style={{ ...smsInputStyle, paddingLeft: 36, letterSpacing: 1, fontFamily: 'Monaco, monospace', flex: 1 }}
                      disabled={resetLoading}
                    />
                    <button
                      onClick={handleResetSendCode}
                      disabled={resetLoading || resetCountdown > 0}
                      style={{
                        marginLeft: 8, padding: '8px 14px', borderRadius: 6, border: 'none',
                        background: resetCountdown > 0 ? 'var(--bg-hover)' : 'rgba(245,158,11,0.2)',
                        color: resetCountdown > 0 ? 'var(--text-muted)' : '#f59e0b',
                        fontSize: 12, cursor: resetCountdown > 0 ? 'default' : 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 500,
                      }}
                    >
                      {resetCountdown > 0 ? `${resetCountdown}s` : '重新发送'}
                    </button>
                  </div>

                  {/* 新密码 */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Lock size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                    <input
                      type={showResetPwd ? 'text' : 'password'}
                      placeholder="新密码（至少 6 位）"
                      value={resetNewPwd}
                      onChange={e => setResetNewPwd(e.target.value)}
                      style={{ ...smsInputStyle, paddingLeft: 36, paddingRight: 40, width: '100%' }}
                      disabled={resetLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPwd(!showResetPwd)}
                      style={{ position: 'absolute', right: 10, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, zIndex: 2 }}
                    >
                      {showResetPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {/* 确认密码 */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Lock size={16} style={{ position: 'absolute', left: 12, color: '#64748b', zIndex: 2 }} />
                    <input
                      type={showResetPwd ? 'text' : 'password'}
                      placeholder="确认新密码"
                      value={resetConfirmPwd}
                      onChange={e => setResetConfirmPwd(e.target.value)}
                      style={{ ...smsInputStyle, paddingLeft: 36, width: '100%' }}
                      disabled={resetLoading}
                      onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                    />
                  </div>

                  {resetError && (
                    <div style={{ fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <AlertCircle size={12} /><span>{resetError}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  style={{
                    width: '100%', padding: 12, background: resetLoading ? '#4a3800' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', border: 'none', borderRadius: 8, cursor: resetLoading ? 'wait' : 'pointer',
                    fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {resetLoading ? <><Loader2 size={16} className="spin" /> 重置中...</> : '重置密码'}
                </button>
              </>
            )}

            {/* Step 4: 成功 */}
            {resetStep === 4 && (
              <>
                <h2 style={styles.modalTitle}>密码重置成功</h2>
                <p style={{ ...styles.modalSubtitle, color: '#10b981' }}>请使用新密码登录</p>
                <button
                  onClick={closeResetModal}
                  style={{
                    width: '100%', padding: 12, marginTop: 12,
                    background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontWeight: 600, fontSize: 14,
                  }}
                >
                  返回登录
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* 动画 CSS */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 
          0% { left: -100%; }
          100% { left: 150%; }
        }
        .spin { animation: spin 1s linear infinite; }
        input::placeholder { color: #525252; }
      `}</style>
    </div>
  );
}

// 📱 短信输入框样式
const smsInputStyle = {
  padding: '10px 12px',
  fontSize: 13,
  color: 'var(--text-body)',
  background: 'var(--modal-input-bg)',
  border: '1px solid var(--modal-input-border)',
  borderRadius: 6,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const styles = {
  container: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // 蓝色背景，右上角和左下角有高亮渐变
    background: `
      radial-gradient(circle at 10% 90%, rgba(30, 58, 138, 0.6) 0%, transparent 40%),
      radial-gradient(circle at 90% 10%, rgba(30, 58, 138, 0.4) 0%, transparent 40%),
      #0f172a
    `, 
    position: 'relative',
    overflow: 'hidden',
    userSelect: 'none',
  },
  
  // 主卡片：21:9 比例 (1092 x 468) -> 约等于 2.33
  cardContainer: {
    width: '1092px', 
    height: '468px', 
    position: 'relative',
    borderRadius: '0px', // 直角
    boxShadow: '0 50px 100px -20px rgba(0, 0, 0, 0.7)', // 深邃阴影
    background: '#000', // 默认黑底
    overflow: 'hidden',
    userSelect: 'none',
  },
  
  // 背景层
  bgLayer: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 1,
  },
  bgImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  placeholderBg: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
    position: 'relative',
  },
  globalOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.1)', // 轻微压暗，保证图片不过曝
  },
  
  // 🆕 图片加载中的闪光效果
  shimmerOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  shimmerBar: {
    position: 'absolute',
    top: 0, left: '-100%',
    width: '50%', height: '100%',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
    animation: 'shimmer 2s ease-in-out infinite',
    transform: 'skewX(-20deg)',
  },

  // 浮动内容面板 (左侧渐变)
  overlayPanel: {
    position: 'absolute',
    top: 0, left: 0, bottom: 0,
    width: '350px',
    zIndex: 10,
    display: 'flex',
  },
  
  // 渐变背景核心：左侧实色 -> 中间透明过渡
  gradientBackground: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    // 左边 70% 是较深的半透明黑/蓝，右边渐变到完全透明
    background: 'linear-gradient(90deg, rgba(8,8,8,0.95) 0%, rgba(15,20,30,0.95) 60%, rgba(15,20,30,0) 100%)',
    // 也可以加一点毛玻璃 (backdrop-filter 对性能有轻微影响，视情况而定)
    backdropFilter: 'blur(3px)', 
  },

  // 内容区域
  contentArea: {
    position: 'relative',
    zIndex: 2,
    width: '340px', // 内容实际宽度
    height: '100%',
    padding: '30px 0 30px 50px', // 减小上下内边距
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transform: 'translateY(-20px)', // 整体向上移动 20px
  },

  headerSection: {
    marginBottom: '20px',
    marginTop: '0px',
    userSelect: 'none',
    overflow: 'hidden',
    maxWidth: '340px',
  },
  logoContainer: {
    height: '80px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',        // 多个 Logo 之间留 2px
    maxWidth: '400px', // 三个并排时整体不越过左栏
    userSelect: 'none',
  },
  customLogo: {
    // 写死 height：无固有尺寸的 SVG（只有 viewBox）在 flex 里只给 max-* 会塌成 0
    height: '80px',
    width: 'auto',
    maxWidth: '400px',
    minWidth: 0,       // 允许在放不下时收缩，不撑破左栏
    objectFit: 'contain',
    display: 'block',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  placeholderLogo: {
    height: '48px',
    lineHeight: '48px',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#64748b',
    marginBottom: '12px',
    userSelect: 'none',
  },
  titleGroup: {
    position: 'relative',
    display: 'block',
    userSelect: 'none',
    maxWidth: '340px',
    overflow: 'hidden',
  },
  appName: {
    margin: 0,
    fontSize: '40px',
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: '1.1',
    letterSpacing: '-0.5px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
  },
  version: {
    margin: '4px 0 0',
    fontSize: '12px',
    fontWeight: '400',
    color: '#94a3b8',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    userSelect: 'none',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '320px',
    padding: '0 20px 0 0',
  },
  inputGroup: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxWidth: '260px',
  },
  inputIcon: {
    position: 'absolute',
    left: '10px',
    color: '#64748b',
    pointerEvents: 'none',
    zIndex: 2,
  },
  input: {
    width: '100%',
    padding: '8px 10px 8px 36px',
    fontSize: '13px',
    color: '#e2e8f0',
    background: 'rgba(255,255,255,0.08)', // 很淡的背景
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    outline: 'none',
    transition: 'all 0.2s',
  },
  eyeBtn: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: 0,
    zIndex: 2,
    userSelect: 'none',
  },

  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // 两端对齐
    marginTop: '6px',    maxWidth: '260px',    userSelect: 'none',
  },
  textLinkBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '0',
    textAlign: 'left',
    transition: 'color 0.2s',
    userSelect: 'none',
  },
  submitBtn: {
    padding: '8px 24px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    background: '#e14343', // 稍微亮一点的红色
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    userSelect: 'none',
  },
  error: {
    fontSize: '11px',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '-4px',
    userSelect: 'none',
  },

  // 🆕 右角标区域（在组织区上方，透明背景）
  cornerLogoSection: {
    position: 'absolute',
    bottom: '60px', // 在组织区上方留出空间
    right: '25px',
    zIndex: 10,
    userSelect: 'none',
  },
  
  cornerLogoImg: {
    width: 'auto',
    height: '120px', // 增大默认高度
    maxWidth: '300px',
    objectFit: 'contain',
    display: 'block',
    userSelect: 'none',
    pointerEvents: 'none',
    transition: 'all 0.3s ease',
  },
  
  // 组织展示区
  organizationsSection: {
    position: 'absolute',
    bottom: '16px',
    right: '25px',
    zIndex: 10,
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '20px',
    overflowX: 'auto',
    overflowY: 'hidden',
    maxWidth: '60%',
    padding: '4px 8px',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.15) transparent',
    userSelect: 'none',
  },
  
  organizationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  
  organizationLogo: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.03)',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  
  organizationName: {
    fontSize: '11px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.2px',
    userSelect: 'none',
  },
  
  // 角标文字位置
  cornerTextBottomRight: {
    position: 'absolute',
    bottom: '20px',
    right: '25px',
    zIndex: 10, // 在背景上
    fontSize: '11px',
    color: 'rgba(255,255,255,0.6)',
    letterSpacing: '0.5px',
    userSelect: 'none',
  },
  cornerTextTopRight: {
    position: 'absolute',
    top: '25px',
    right: '25px',
    zIndex: 10,
    writingMode: 'vertical-lr', // 竖排设计感? 不，Resolve 是横排三个点logo
    // 如果是 Resolve 风格，右上角通常是 Logo 图标。但这里用文字代替
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#f59e0b', // 金色点缀
    fontSize: '12px',
    fontWeight: 'bold',
    userSelect: 'none',
  },

  // 弹窗通用 (不改动太大，只微调层级)
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(5px)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCard: {
    width: '360px',
    background: 'var(--modal-surface)',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid var(--border-strong)',
    textAlign: 'center',
    position: 'relative',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
  },
  modalIcon: { marginBottom: '16px' },
  modalTitle: { margin: '0 0 8px', fontSize: '20px', color: 'var(--text-primary)' },
  modalSubtitle: { margin: '0 0 16px', fontSize: '14px', color: 'var(--text-secondary)' },
  modalText: { margin: '0 0 24px', fontSize: '13px', color: 'var(--text-body)', lineHeight: '1.5' },
  featureList: { textAlign: 'left', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-body)' },
  buttonGroup: { display: 'flex', gap: '12px', flexDirection: 'column' },
  primaryBtn: {
    padding: '12px', background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
  },
  secondaryBtn: {
    padding: '10px', background: 'transparent', color: 'var(--text-secondary)', border: 'none',
    cursor: 'pointer', fontSize: '13px'
  },
  modalBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    padding: '12px 32px', background: '#8b5cf6', color: '#fff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
  }
};